export type DemoScenarioId =
  | 'warm-transfer'
  | 'calendar-booking'
  | 'landline-voice'
  | 'stop-suppression';

export type DemoSpeed = 1 | 4;

export type DemoChannel =
  | 'meta'
  | 'validation'
  | 'voice'
  | 'imessage'
  | 'sms'
  | 'calendar'
  | 'system';

export type DemoProvider =
  | 'meta'
  | 'ipqs'
  | 'sendblue'
  | 'signalhouse'
  | 'retell'
  | 'elevenlabs'
  | 'google_calendar'
  | 'acl_auto';

export type DemoDirection = 'inbound' | 'outbound' | 'internal';
export type DemoEventTone = 'neutral' | 'info' | 'success' | 'warning';

export type DemoEventKind =
  | 'lead_received'
  | 'phone_validated'
  | 'route_selected'
  | 'call_started'
  | 'call_connected'
  | 'call_no_answer'
  | 'message_sent'
  | 'message_delivered'
  | 'message_received'
  | 'ai_replied'
  | 'lead_qualified'
  | 'transfer_ready'
  | 'transfer_started'
  | 'transfer_connected'
  | 'call_ended'
  | 'calendar_offered'
  | 'calendar_booked'
  | 'suppression_applied'
  | 'handoff_completed';

export type DemoLeadStage =
  | 'new'
  | 'checking'
  | 'ready'
  | 'dialing'
  | 'talking'
  | 'texting'
  | 'qualified'
  | 'transfer_ready'
  | 'transferring'
  | 'transferred'
  | 'booking'
  | 'booked'
  | 'voice_only'
  | 'suppressed'
  | 'handed_off';

export type DemoLineType = 'mobile' | 'landline';

export interface DemoPhoneIntel {
  valid: boolean;
  lineType: DemoLineType;
  carrier: string;
  risk: 'low';
  canText: boolean;
}

export interface DemoLead {
  id: string;
  scenarioId: DemoScenarioId;
  name: string;
  initials: string;
  phone: string;
  email: string;
  location: string;
  source: 'Meta lead form';
  campaign: string;
  createdAtLabel: string;
  phoneIntel: DemoPhoneIntel;
  preferredChannel: 'voice' | 'imessage' | 'sms';
}

export interface DemoLeadRuntimeState {
  stage: DemoLeadStage;
  statusLabel: string;
  statusDetail: string;
  activeChannel: DemoChannel | null;
  lastCustomerChannel: DemoChannel | null;
  outcome: string | null;
  contacted: boolean;
  engaged: boolean;
  qualified: boolean;
  suppressed: boolean;
  canText: boolean;
  lastEventAtMs: number | null;
}

export type DemoLeadSnapshot = DemoLead & DemoLeadRuntimeState;

export interface DemoTranscriptLine {
  speaker: 'lead' | 'ai' | 'agent';
  text: string;
}

export interface DemoEvent {
  id: string;
  scenarioId: DemoScenarioId;
  leadId: string;
  atMs: number;
  kind: DemoEventKind;
  channel: DemoChannel;
  provider: DemoProvider;
  direction: DemoDirection;
  title: string;
  detail: string;
  tone: DemoEventTone;
  transcript?: DemoTranscriptLine;
  statePatch?: Partial<DemoLeadRuntimeState>;
}

export interface DemoTransferFallback {
  branchAtMs: number;
  durationMs: number;
  events: readonly DemoEvent[];
}

export interface DemoScenario {
  id: DemoScenarioId;
  title: string;
  shortTitle: string;
  description: string;
  outcomeLabel: string;
  durationMs: number;
  lead: DemoLead;
  initialState: DemoLeadRuntimeState;
  events: readonly DemoEvent[];
  transferFallback?: DemoTransferFallback;
}

export interface DemoSummaryMetrics {
  totalLeads: number;
  contacted: number;
  conversations: number;
  qualified: number;
  calls: number;
  messages: number;
  transfers: number;
  bookings: number;
  suppressed: number;
  attempts: number;
  responseRate: number;
  timeToFirstTouchSeconds: number | null;
  headline: string;
}

export interface DemoActivity extends DemoEvent {
  elapsedLabel: string;
  isLatest: boolean;
}

export type DemoTransferState = 'disabled' | 'waiting' | 'ready' | 'connecting' | 'connected';

export interface DemoTransferStatus {
  available: boolean;
  state: DemoTransferState;
  label: string;
}

type EventInput = Omit<DemoEvent, 'id' | 'scenarioId' | 'leadId'>;

function makeEvent(
  scenarioId: DemoScenarioId,
  leadId: string,
  sequence: number,
  input: EventInput,
): DemoEvent {
  return {
    ...input,
    id: `${scenarioId}-${String(sequence).padStart(2, '0')}`,
    scenarioId,
    leadId,
  };
}

function makeInitialState(canText: boolean): DemoLeadRuntimeState {
  return {
    stage: 'new',
    statusLabel: 'New lead',
    statusDetail: 'Waiting for ACL Auto',
    activeChannel: 'meta',
    lastCustomerChannel: null,
    outcome: null,
    contacted: false,
    engaged: false,
    qualified: false,
    suppressed: false,
    canText,
    lastEventAtMs: null,
  };
}

const warmTransferId = 'warm-transfer' satisfies DemoScenarioId;
const warmTransferLeadId = 'lead-maya-sample';
const warmTransferLead: DemoLead = {
  id: warmTransferLeadId,
  scenarioId: warmTransferId,
  name: 'Maya Carter',
  initials: 'MC',
  phone: '(212) 555-0104',
  email: 'maya.carter@example.com',
  location: 'New York, NY',
  source: 'Meta lead form',
  campaign: 'Mortgage Protection Request - NY',
  createdAtLabel: 'just now',
  phoneIntel: {
    valid: true,
    lineType: 'mobile',
    carrier: 'Sample Mobile',
    risk: 'low',
    canText: true,
  },
  preferredChannel: 'imessage',
};

const warmTransferEvents: readonly DemoEvent[] = [
  makeEvent(warmTransferId, warmTransferLeadId, 1, {
    atMs: 0,
    kind: 'lead_received',
    channel: 'meta',
    provider: 'meta',
    direction: 'inbound',
    title: 'New Meta lead',
    detail: 'Maya requested information about mortgage protection coverage.',
    tone: 'info',
    statePatch: {
      stage: 'checking',
      statusLabel: 'Checking phone',
      statusDetail: 'Validating line type and carrier',
      activeChannel: 'validation',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 2, {
    atMs: 1_200,
    kind: 'phone_validated',
    channel: 'validation',
    provider: 'ipqs',
    direction: 'internal',
    title: 'Mobile number verified',
    detail: 'Active mobile line with a low-risk carrier profile.',
    tone: 'success',
    statePatch: {
      stage: 'ready',
      statusLabel: 'Ready to contact',
      statusDetail: 'Mobile line verified',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 3, {
    atMs: 2_400,
    kind: 'route_selected',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'internal',
    title: 'iMessage route available',
    detail: 'Messaging intelligence confirmed an iMessage-capable destination.',
    tone: 'info',
    statePatch: {
      statusLabel: 'Route selected',
      statusDetail: 'Voice first, iMessage ready',
      activeChannel: 'imessage',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 4, {
    atMs: 4_000,
    kind: 'call_started',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'AI call started',
    detail: 'The voice agent dialed from an ACL Auto local number.',
    tone: 'info',
    statePatch: {
      stage: 'dialing',
      statusLabel: 'Calling now',
      statusDetail: 'Local caller ID via ACL voice',
      activeChannel: 'voice',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 5, {
    atMs: 7_200,
    kind: 'call_connected',
    channel: 'voice',
    provider: 'elevenlabs',
    direction: 'outbound',
    title: 'Conversation connected',
    detail: 'The ACL Auto voice agent began the qualification conversation.',
    tone: 'success',
    transcript: {
      speaker: 'ai',
      text: 'Hi Maya, I am calling about the information you just requested. Did I catch you at an okay time?',
    },
    statePatch: {
      stage: 'talking',
      statusLabel: 'AI conversation live',
      statusDetail: 'Listening for timing and intent',
      contacted: true,
      engaged: true,
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 6, {
    atMs: 13_500,
    kind: 'lead_qualified',
    channel: 'voice',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Lead qualified',
    detail: 'Homeowner, coverage need confirmed, ready to speak with a licensed agent.',
    tone: 'success',
    transcript: {
      speaker: 'lead',
      text: 'Yes, I would like to speak with someone about the coverage options.',
    },
    statePatch: {
      stage: 'qualified',
      statusLabel: 'Qualified',
      statusDetail: 'Ready for a licensed agent',
      qualified: true,
      outcome: 'Qualified conversation',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 7, {
    atMs: 18_500,
    kind: 'transfer_ready',
    channel: 'voice',
    provider: 'retell',
    direction: 'internal',
    title: 'Agent is available',
    detail: 'ACL Auto confirmed the assigned agent can take a warm transfer.',
    tone: 'info',
    statePatch: {
      stage: 'transfer_ready',
      statusLabel: 'Agent available',
      statusDetail: 'Preparing a warm introduction',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 8, {
    atMs: 21_500,
    kind: 'transfer_started',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'Warm transfer started',
    detail: 'The AI introduced Maya before joining the assigned agent.',
    tone: 'info',
    transcript: {
      speaker: 'ai',
      text: 'Perfect. I have your agent ready, and I am bringing them onto the call now.',
    },
    statePatch: {
      stage: 'transferring',
      statusLabel: 'Transferring live',
      statusDetail: 'Connecting the assigned agent',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 9, {
    atMs: 25_000,
    kind: 'transfer_connected',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'Agent connected',
    detail: 'Warm transfer completed with lead context attached.',
    tone: 'success',
    statePatch: {
      stage: 'transferred',
      statusLabel: 'Live transfer complete',
      statusDetail: 'Lead and agent are connected',
      outcome: 'Warm transfer',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 10, {
    atMs: 30_500,
    kind: 'call_ended',
    channel: 'voice',
    provider: 'retell',
    direction: 'internal',
    title: 'AI left the call',
    detail: 'Transcript, qualification notes, and call outcome were saved.',
    tone: 'neutral',
    statePatch: {
      statusDetail: 'Conversation record saved',
      activeChannel: 'system',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 11, {
    atMs: 34_000,
    kind: 'handoff_completed',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Lead handed off',
    detail: 'The client record now includes the full AI conversation summary.',
    tone: 'success',
    statePatch: {
      stage: 'handed_off',
      statusLabel: 'Handed to agent',
      statusDetail: 'Warm transfer completed',
      outcome: 'Warm transfer complete',
    },
  }),
];

const warmTransferFallbackEvents: readonly DemoEvent[] = [
  makeEvent(warmTransferId, warmTransferLeadId, 20, {
    atMs: 18_500,
    kind: 'calendar_offered',
    channel: 'calendar',
    provider: 'google_calendar',
    direction: 'outbound',
    title: 'Calendar fallback offered',
    detail: 'No agent was available, so ACL Auto offered the next open appointment.',
    tone: 'info',
    transcript: {
      speaker: 'ai',
      text: 'Your agent is tied up, but I can reserve their next opening for you.',
    },
    statePatch: {
      stage: 'booking',
      statusLabel: 'Finding a time',
      statusDetail: 'Checking the agent calendar',
      activeChannel: 'calendar',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 21, {
    atMs: 24_000,
    kind: 'calendar_booked',
    channel: 'calendar',
    provider: 'google_calendar',
    direction: 'internal',
    title: 'Appointment booked',
    detail: 'Maya selected tomorrow at 10:30 AM.',
    tone: 'success',
    statePatch: {
      stage: 'booked',
      statusLabel: 'Appointment booked',
      statusDetail: 'Tomorrow at 10:30 AM',
      outcome: 'Google Calendar booking',
    },
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 22, {
    atMs: 28_000,
    kind: 'message_sent',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'outbound',
    title: 'Confirmation sent',
    detail: 'An iMessage confirmation included the calendar details.',
    tone: 'success',
  }),
  makeEvent(warmTransferId, warmTransferLeadId, 23, {
    atMs: 32_000,
    kind: 'handoff_completed',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Booked lead handed off',
    detail: 'The client record includes notes and the confirmed appointment.',
    tone: 'success',
    statePatch: {
      stage: 'handed_off',
      statusLabel: 'Booked and handed off',
      statusDetail: 'Calendar fallback completed',
      activeChannel: 'system',
      outcome: 'Appointment booked',
    },
  }),
];

const calendarBookingId = 'calendar-booking' satisfies DemoScenarioId;
const calendarBookingLeadId = 'lead-jordan-demo';
const calendarBookingLead: DemoLead = {
  id: calendarBookingLeadId,
  scenarioId: calendarBookingId,
  name: 'Jordan Ellis',
  initials: 'JE',
  phone: '(312) 555-0112',
  email: 'jordan.ellis@example.com',
  location: 'Chicago, IL',
  source: 'Meta lead form',
  campaign: 'Coverage Request - IL',
  createdAtLabel: '1 min ago',
  phoneIntel: {
    valid: true,
    lineType: 'mobile',
    carrier: 'Example Wireless',
    risk: 'low',
    canText: true,
  },
  preferredChannel: 'sms',
};

const calendarBookingEvents: readonly DemoEvent[] = [
  makeEvent(calendarBookingId, calendarBookingLeadId, 1, {
    atMs: 0,
    kind: 'lead_received',
    channel: 'meta',
    provider: 'meta',
    direction: 'inbound',
    title: 'New Meta lead',
    detail: 'Jordan submitted the coverage information request form.',
    tone: 'info',
    statePatch: {
      stage: 'checking',
      statusLabel: 'Checking phone',
      statusDetail: 'Validating line type and carrier',
      activeChannel: 'validation',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 2, {
    atMs: 1_100,
    kind: 'phone_validated',
    channel: 'validation',
    provider: 'ipqs',
    direction: 'internal',
    title: 'Mobile number verified',
    detail: 'Active mobile line, safe for voice and messaging.',
    tone: 'success',
    statePatch: {
      stage: 'ready',
      statusLabel: 'Ready to contact',
      statusDetail: 'Mobile line verified',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 3, {
    atMs: 2_200,
    kind: 'route_selected',
    channel: 'sms',
    provider: 'signalhouse',
    direction: 'internal',
    title: '10DLC SMS fallback ready',
    detail: 'iMessage was unavailable, so the approved carrier SMS route was selected.',
    tone: 'info',
    statePatch: {
      statusLabel: 'Route selected',
      statusDetail: 'Voice first, SMS fallback ready',
      activeChannel: 'sms',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 4, {
    atMs: 3_600,
    kind: 'call_started',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'AI call started',
    detail: 'The voice agent placed the first high-intent contact attempt.',
    tone: 'info',
    statePatch: {
      stage: 'dialing',
      statusLabel: 'Calling now',
      statusDetail: 'First contact attempt',
      activeChannel: 'voice',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 5, {
    atMs: 9_000,
    kind: 'call_no_answer',
    channel: 'voice',
    provider: 'retell',
    direction: 'internal',
    title: 'No answer',
    detail: 'The call ended without leaving a second voicemail.',
    tone: 'neutral',
    statePatch: {
      stage: 'texting',
      statusLabel: 'Switching to SMS',
      statusDetail: 'Continuing on the approved fallback',
      activeChannel: 'sms',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 6, {
    atMs: 10_500,
    kind: 'message_sent',
    channel: 'sms',
    provider: 'signalhouse',
    direction: 'outbound',
    title: 'Follow-up text sent',
    detail: 'The SMS fallback sent a compliant 10DLC message after the missed call.',
    tone: 'info',
    transcript: {
      speaker: 'ai',
      text: 'Hi Jordan, I just called about the quote you requested. Is now a good time to help?',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 7, {
    atMs: 12_200,
    kind: 'message_delivered',
    channel: 'sms',
    provider: 'signalhouse',
    direction: 'internal',
    title: 'SMS delivered',
    detail: 'Carrier delivery confirmed.',
    tone: 'success',
    statePatch: {
      contacted: true,
      statusLabel: 'Message delivered',
      statusDetail: 'Waiting for a reply',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 8, {
    atMs: 17_500,
    kind: 'message_received',
    channel: 'sms',
    provider: 'signalhouse',
    direction: 'inbound',
    title: 'Lead replied',
    detail: 'Jordan asked to schedule a conversation instead.',
    tone: 'success',
    transcript: {
      speaker: 'lead',
      text: 'I cannot talk right now. Can I pick a time for tomorrow?',
    },
    statePatch: {
      engaged: true,
      stage: 'texting',
      statusLabel: 'Text conversation live',
      statusDetail: 'Finding a time that works',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 9, {
    atMs: 20_500,
    kind: 'ai_replied',
    channel: 'sms',
    provider: 'acl_auto',
    direction: 'outbound',
    title: 'AI replied',
    detail: 'ACL Auto moved the conversation into appointment scheduling.',
    tone: 'info',
    transcript: {
      speaker: 'ai',
      text: 'Absolutely. I can reserve 9:00 AM or 2:30 PM tomorrow. Which is better?',
    },
    statePatch: {
      stage: 'booking',
      statusLabel: 'Offering times',
      statusDetail: 'Two live calendar slots available',
      activeChannel: 'calendar',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 10, {
    atMs: 23_500,
    kind: 'calendar_offered',
    channel: 'calendar',
    provider: 'google_calendar',
    direction: 'internal',
    title: 'Calendar checked',
    detail: 'Both offered times remained available on the assigned agent calendar.',
    tone: 'info',
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 11, {
    atMs: 28_500,
    kind: 'message_received',
    channel: 'sms',
    provider: 'signalhouse',
    direction: 'inbound',
    title: 'Time selected',
    detail: 'Jordan chose the afternoon appointment.',
    tone: 'success',
    transcript: {
      speaker: 'lead',
      text: '2:30 works for me.',
    },
    statePatch: {
      qualified: true,
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 12, {
    atMs: 31_000,
    kind: 'calendar_booked',
    channel: 'calendar',
    provider: 'google_calendar',
    direction: 'internal',
    title: 'Appointment booked',
    detail: 'Tomorrow at 2:30 PM was added to the assigned agent calendar.',
    tone: 'success',
    statePatch: {
      stage: 'booked',
      statusLabel: 'Appointment booked',
      statusDetail: 'Tomorrow at 2:30 PM',
      outcome: 'Google Calendar booking',
    },
  }),
  makeEvent(calendarBookingId, calendarBookingLeadId, 13, {
    atMs: 35_000,
    kind: 'handoff_completed',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Booked lead handed off',
    detail: 'Conversation history and appointment details were attached.',
    tone: 'success',
    statePatch: {
      stage: 'handed_off',
      statusLabel: 'Booked and handed off',
      statusDetail: 'Calendar confirmation delivered',
      activeChannel: 'system',
      outcome: 'Appointment booked',
    },
  }),
];

const landlineVoiceId = 'landline-voice' satisfies DemoScenarioId;
const landlineVoiceLeadId = 'lead-taylor-preview';
const landlineVoiceLead: DemoLead = {
  id: landlineVoiceLeadId,
  scenarioId: landlineVoiceId,
  name: 'Taylor Nguyen',
  initials: 'TN',
  phone: '(404) 555-0138',
  email: 'taylor.nguyen@example.com',
  location: 'Atlanta, GA',
  source: 'Meta lead form',
  campaign: 'Mortgage Protection Request - GA',
  createdAtLabel: '3 min ago',
  phoneIntel: {
    valid: true,
    lineType: 'landline',
    carrier: 'Example Local Exchange',
    risk: 'low',
    canText: false,
  },
  preferredChannel: 'voice',
};

const landlineVoiceEvents: readonly DemoEvent[] = [
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 1, {
    atMs: 0,
    kind: 'lead_received',
    channel: 'meta',
    provider: 'meta',
    direction: 'inbound',
    title: 'New Meta lead',
    detail: 'Taylor requested information about protecting a new mortgage.',
    tone: 'info',
    statePatch: {
      stage: 'checking',
      statusLabel: 'Checking phone',
      statusDetail: 'Validating line type and carrier',
      activeChannel: 'validation',
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 2, {
    atMs: 1_300,
    kind: 'phone_validated',
    channel: 'validation',
    provider: 'ipqs',
    direction: 'internal',
    title: 'Landline detected',
    detail: 'Active fixed line; messaging attempts were automatically blocked.',
    tone: 'warning',
    statePatch: {
      stage: 'voice_only',
      statusLabel: 'Voice-only route',
      statusDetail: 'Texting disabled for this number',
      activeChannel: 'voice',
      canText: false,
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 3, {
    atMs: 2_700,
    kind: 'route_selected',
    channel: 'voice',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Voice-only route selected',
    detail: 'ACL Auto skipped every messaging channel for this landline.',
    tone: 'info',
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 4, {
    atMs: 4_000,
    kind: 'call_started',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'AI call started',
    detail: 'The voice agent dialed from an ACL Auto local number.',
    tone: 'info',
    statePatch: {
      stage: 'dialing',
      statusLabel: 'Calling landline',
      statusDetail: 'Voice is the only permitted route',
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 5, {
    atMs: 8_500,
    kind: 'call_no_answer',
    channel: 'voice',
    provider: 'retell',
    direction: 'internal',
    title: 'First call missed',
    detail: 'No text was attempted after the missed call.',
    tone: 'neutral',
    statePatch: {
      stage: 'voice_only',
      statusLabel: 'Voice follow-up queued',
      statusDetail: 'Next call uses a different approved window',
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 6, {
    atMs: 14_000,
    kind: 'call_started',
    channel: 'voice',
    provider: 'retell',
    direction: 'outbound',
    title: 'Second call started',
    detail: 'ACL Auto made the scheduled follow-up attempt.',
    tone: 'info',
    statePatch: {
      stage: 'dialing',
      statusLabel: 'Calling again',
      statusDetail: 'Second voice-only attempt',
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 7, {
    atMs: 18_500,
    kind: 'call_connected',
    channel: 'voice',
    provider: 'elevenlabs',
    direction: 'outbound',
    title: 'Conversation connected',
    detail: 'Taylor answered and began speaking with the ACL Auto voice agent.',
    tone: 'success',
    transcript: {
      speaker: 'ai',
      text: 'Hi Taylor, I am following up on the mortgage protection request you sent in.',
    },
    statePatch: {
      stage: 'talking',
      statusLabel: 'AI conversation live',
      statusDetail: 'Qualifying by voice',
      contacted: true,
      engaged: true,
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 8, {
    atMs: 24_500,
    kind: 'lead_qualified',
    channel: 'voice',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Lead qualified',
    detail: 'Mortgage balance confirmed and available for a licensed-agent callback.',
    tone: 'success',
    statePatch: {
      stage: 'qualified',
      statusLabel: 'Qualified by voice',
      statusDetail: 'Agent callback requested',
      qualified: true,
      outcome: 'Qualified callback',
    },
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 9, {
    atMs: 29_500,
    kind: 'call_ended',
    channel: 'voice',
    provider: 'retell',
    direction: 'internal',
    title: 'Call completed',
    detail: 'The qualification summary and callback request were saved.',
    tone: 'neutral',
  }),
  makeEvent(landlineVoiceId, landlineVoiceLeadId, 10, {
    atMs: 33_000,
    kind: 'handoff_completed',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Callback handed off',
    detail: 'The assigned agent received the voice transcript and callback request.',
    tone: 'success',
    statePatch: {
      stage: 'handed_off',
      statusLabel: 'Callback handed off',
      statusDetail: 'No messaging spend used',
      activeChannel: 'system',
      outcome: 'Qualified callback',
    },
  }),
];

const stopSuppressionId = 'stop-suppression' satisfies DemoScenarioId;
const stopSuppressionLeadId = 'lead-casey-test';
const stopSuppressionLead: DemoLead = {
  id: stopSuppressionLeadId,
  scenarioId: stopSuppressionId,
  name: 'Casey Monroe',
  initials: 'CM',
  phone: '(646) 555-0159',
  email: 'casey.monroe@example.com',
  location: 'Brooklyn, NY',
  source: 'Meta lead form',
  campaign: 'Coverage Request - NY',
  createdAtLabel: '5 min ago',
  phoneIntel: {
    valid: true,
    lineType: 'mobile',
    carrier: 'Sample Cellular',
    risk: 'low',
    canText: true,
  },
  preferredChannel: 'imessage',
};

const stopSuppressionEvents: readonly DemoEvent[] = [
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 1, {
    atMs: 0,
    kind: 'lead_received',
    channel: 'meta',
    provider: 'meta',
    direction: 'inbound',
    title: 'New Meta lead',
    detail: 'Casey submitted the coverage information request form.',
    tone: 'info',
    statePatch: {
      stage: 'checking',
      statusLabel: 'Checking phone',
      statusDetail: 'Validating line type and carrier',
      activeChannel: 'validation',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 2, {
    atMs: 1_100,
    kind: 'phone_validated',
    channel: 'validation',
    provider: 'ipqs',
    direction: 'internal',
    title: 'Mobile number verified',
    detail: 'Active mobile line with low delivery risk.',
    tone: 'success',
    statePatch: {
      stage: 'ready',
      statusLabel: 'Ready to contact',
      statusDetail: 'Mobile line verified',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 3, {
    atMs: 2_200,
    kind: 'route_selected',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'internal',
    title: 'iMessage route selected',
    detail: 'Messaging intelligence confirmed iMessage availability.',
    tone: 'info',
    statePatch: {
      stage: 'texting',
      statusLabel: 'Starting conversation',
      statusDetail: 'iMessage route ready',
      activeChannel: 'imessage',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 4, {
    atMs: 4_000,
    kind: 'message_sent',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'outbound',
    title: 'First message sent',
    detail: 'ACL Auto sent one consent-aligned follow-up.',
    tone: 'info',
    transcript: {
      speaker: 'ai',
      text: 'Hi Casey, I am following up on the information you requested. Reply STOP to opt out.',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 5, {
    atMs: 5_500,
    kind: 'message_delivered',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'internal',
    title: 'iMessage delivered',
    detail: 'Delivery was confirmed on iMessage.',
    tone: 'success',
    statePatch: {
      contacted: true,
      statusLabel: 'Message delivered',
      statusDetail: 'Waiting for a reply',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 6, {
    atMs: 10_500,
    kind: 'message_received',
    channel: 'imessage',
    provider: 'sendblue',
    direction: 'inbound',
    title: 'Opt-out received',
    detail: 'Casey replied with a recognized stop keyword.',
    tone: 'warning',
    transcript: {
      speaker: 'lead',
      text: 'STOP',
    },
    statePatch: {
      engaged: true,
      statusLabel: 'Opt-out detected',
      statusDetail: 'Stopping every automated channel',
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 7, {
    atMs: 10_650,
    kind: 'suppression_applied',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Suppression applied',
    detail: 'Voice, iMessage, and SMS outreach were disabled in 150 ms.',
    tone: 'success',
    statePatch: {
      stage: 'suppressed',
      statusLabel: 'Suppressed',
      statusDetail: 'No further outreach permitted',
      activeChannel: 'system',
      outcome: 'Opted out',
      suppressed: true,
      canText: false,
    },
  }),
  makeEvent(stopSuppressionId, stopSuppressionLeadId, 8, {
    atMs: 13_500,
    kind: 'handoff_completed',
    channel: 'system',
    provider: 'acl_auto',
    direction: 'internal',
    title: 'Suppression synchronized',
    detail: 'The opt-out status was attached to the client record for future protection.',
    tone: 'neutral',
    statePatch: {
      statusDetail: 'Suppression synchronized',
    },
  }),
];

export const DEMO_SCENARIOS: readonly DemoScenario[] = [
  {
    id: warmTransferId,
    title: 'New lead to live transfer',
    shortTitle: 'Warm transfer',
    description: 'A new mobile lead answers the AI assistant, qualifies, and connects live with the agent.',
    outcomeLabel: 'Transferred live',
    durationMs: 36_000,
    lead: warmTransferLead,
    initialState: makeInitialState(true),
    events: warmTransferEvents,
    transferFallback: {
      branchAtMs: 18_500,
      durationMs: 34_000,
      events: warmTransferFallbackEvents,
    },
  },
  {
    id: calendarBookingId,
    title: 'Missed call to booked appointment',
    shortTitle: 'Book appointment',
    description: 'After a missed call, a follow-up conversation offers available time and books the appointment.',
    outcomeLabel: 'Appointment booked',
    durationMs: 37_000,
    lead: calendarBookingLead,
    initialState: makeInitialState(true),
    events: calendarBookingEvents,
  },
  {
    id: landlineVoiceId,
    title: 'Landline to voice follow-up',
    shortTitle: 'Landline follow-up',
    description: 'A landline skips messaging, receives voice follow-up, and becomes a qualified callback.',
    outcomeLabel: 'Qualified callback',
    durationMs: 35_000,
    lead: landlineVoiceLead,
    initialState: makeInitialState(false),
    events: landlineVoiceEvents,
  },
  {
    id: stopSuppressionId,
    title: 'Opt-out honored immediately',
    shortTitle: 'Honor opt-out',
    description: 'An opt-out immediately stops automated outreach and safely closes the conversation.',
    outcomeLabel: 'Suppressed safely',
    durationMs: 15_000,
    lead: stopSuppressionLead,
    initialState: makeInitialState(true),
    events: stopSuppressionEvents,
  },
] as const;

export function findDemoScenario(scenarioId: DemoScenarioId): DemoScenario {
  return DEMO_SCENARIOS.find((scenario) => scenario.id === scenarioId) ?? DEMO_SCENARIOS[0];
}

export function getScenarioEvents(
  scenario: DemoScenario,
  transferAvailable = true,
): readonly DemoEvent[] {
  if (transferAvailable || !scenario.transferFallback) {
    return scenario.events;
  }

  return [
    ...scenario.events.filter((event) => event.atMs < scenario.transferFallback!.branchAtMs),
    ...scenario.transferFallback.events,
  ].sort((a, b) => a.atMs - b.atMs);
}

export function getScenarioDuration(scenario: DemoScenario, transferAvailable = true): number {
  if (!transferAvailable && scenario.transferFallback) {
    return scenario.transferFallback.durationMs;
  }

  return scenario.durationMs;
}

export function getVisibleEvents(
  scenario: DemoScenario,
  clockMs: number,
  transferAvailable = true,
): readonly DemoEvent[] {
  return getScenarioEvents(scenario, transferAvailable).filter((event) => event.atMs <= clockMs);
}

export function deriveLeadSnapshot(
  scenario: DemoScenario,
  events: readonly DemoEvent[],
): DemoLeadSnapshot {
  const runtimeState = events.reduce<DemoLeadRuntimeState>(
    (state, event) => {
      const isCustomerChannel = ['voice', 'imessage', 'sms', 'calendar'].includes(event.channel);
      return {
        ...state,
        ...event.statePatch,
        lastCustomerChannel: isCustomerChannel ? event.channel : state.lastCustomerChannel,
        lastEventAtMs: event.atMs,
      };
    },
    { ...scenario.initialState },
  );

  return {
    ...scenario.lead,
    ...runtimeState,
  };
}

export function deriveSummaryMetrics(
  events: readonly DemoEvent[],
  snapshot: DemoLeadSnapshot,
): DemoSummaryMetrics {
  const count = (kind: DemoEventKind) => events.filter((event) => event.kind === kind).length;
  const leadEvent = events.find((event) => event.kind === 'lead_received');
  const firstTouch = events.find(
    (event) => event.kind === 'call_started' || event.kind === 'message_sent',
  );
  const totalLeads = leadEvent ? 1 : 0;
  const conversations = events.some(
    (event) => event.kind === 'call_connected' || event.kind === 'message_received',
  )
    ? 1
    : 0;
  const calls = count('call_started');
  const messages = count('message_sent');

  return {
    totalLeads,
    contacted: snapshot.contacted ? 1 : 0,
    conversations,
    qualified: snapshot.qualified ? 1 : 0,
    calls,
    messages,
    transfers: count('transfer_connected'),
    bookings: count('calendar_booked'),
    suppressed: snapshot.suppressed ? 1 : 0,
    attempts: calls + messages,
    responseRate: totalLeads > 0 ? conversations * 100 : 0,
    timeToFirstTouchSeconds:
      leadEvent && firstTouch ? Math.round((firstTouch.atMs - leadEvent.atMs) / 100) / 10 : null,
    headline: snapshot.statusLabel,
  };
}

export function deriveActivity(events: readonly DemoEvent[]): readonly DemoActivity[] {
  return [...events]
    .reverse()
    .map((event, index) => ({
      ...event,
      elapsedLabel: formatDemoClock(event.atMs),
      isLatest: index === 0,
    }));
}

export function deriveTransferStatus(
  events: readonly DemoEvent[],
  transferAvailable: boolean,
): DemoTransferStatus {
  if (!transferAvailable) {
    return {
      available: false,
      state: 'disabled',
      label: 'Calendar fallback active',
    };
  }

  if (events.some((event) => event.kind === 'transfer_connected')) {
    return { available: true, state: 'connected', label: 'Agent connected' };
  }

  if (events.some((event) => event.kind === 'transfer_started')) {
    return { available: true, state: 'connecting', label: 'Connecting agent' };
  }

  if (events.some((event) => event.kind === 'transfer_ready')) {
    return { available: true, state: 'ready', label: 'Agent ready' };
  }

  return { available: true, state: 'waiting', label: 'Transfer routing ready' };
}

export function formatDemoClock(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
